import { Fragment, useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ReferenceTableDialog from "../components/modules/referenceTableDialog";

import "../css/databasePage.css";

const LINKS = {
  gbd: "https://ghdx.healthdata.org/gbd-2019",
  ithim: "https://www.mrc-epid.cam.ac.uk/research/research-areas/public-health-modelling/ithim/",
  heat: "https://www.who.int/tools/heat-for-walking-and-cycling",
  eie: "https://insights.sustainability.google/",
  curb: "https://www.c40knowledgehub.org/s/article/Climate-action-for-URBan-sustainability-CURB-scenario-planning-tool?language=en_US",
  icct: "https://theicct.org/",
  sandia: "https://www.sandia.gov/",
  greet: "https://greet.es.anl.gov/",
};

// Section 4: default data, grouped by data type
const DEFAULT_DATA = [
  {
    group: "Population",
    rows: [
      { item: "National - Population and Age structure", source: "IHME; Global Burden of Disease (GBD) National Population; 2019", url: LINKS.gbd },
    ],
  },
  {
    group: "Health",
    rows: [
      { item: "Mortality rates - national", source: "IHME; Global Burden of Disease (GBD) Deathrate Data; 2019", url: LINKS.gbd },
      { item: "Years lived with disability (YLDs) - national", source: "IHME; Global Burden of Disease (GBD) Deathrate Data & National YLDs; 2019", url: LINKS.gbd },
      { item: "ITHIM physical activity risk reduction factors", source: "University of Cambridge CEDAR; ITHIM model; 2018", url: LINKS.ithim },
      { item: "Physical activity benefit parameters", source: "World Health Organization; Health Economic Assessment Tool (HEAT) for walking and cycling; 2017", url: LINKS.heat },
    ],
  },
  {
    group: "Mobility",
    rows: [
      { item: "Mode share", source: "Google Environmental Insights Explorer (extracted in June 2023)", url: LINKS.eie },
      { item: "Age range of cyclists and pedestrians", source: "World Health Organization; Health Economic Assessment Tool (HEAT) for walking and cycling; 2017", url: LINKS.heat },
      { item: "Default cyclist and pedestrian population composition", source: "C40 assumption based on the population and age structure" },
      { item: "Minutes of activity per cyclist profile", source: "Google Environmental Insights Explorer (extracted in June 2023)", url: LINKS.eie },
      { item: "Default trips distance", source: "Google Environmental Insights Explorer (extracted in June 2023)", url: LINKS.eie },
      { item: "Default trips per year per user", source: "Assumption of 250 trips per year per user based on World Health Organization; Health Economic Assessment Tool (HEAT) for walking and cycling; 2017", url: LINKS.heat },
    ],
  },
  {
    group: "Emissions",
    rows: [
      { item: "Vehicle load factor", source: "C40; Climate Action for Urban Sustainability Database, Regional Assumptions; 2016", url: LINKS.curb },
      { item: "Fuel fleet composition", source: "ICCT; 2012", url: LINKS.icct },
      { item: "Vehicle fuel efficiency per vehicle class", source: "National Transit Database and Sandia National Database; 2016", url: LINKS.sandia },
      { item: "Per mile fuel emissions", source: "Argonne Lab; GREET Model; 2018", url: LINKS.greet },
    ],
  },
];

// Step 1: summary of the actions table (src/content/tables/actions.md), by indicator. Short names; the PDF has the full ones
const ACTIONS_BY_INDICATOR = [
  {
    group: "Mobility",
    indicators: [
      { name: "Retail quality", actions: ["Add retail space", "Increase residential density", "Increase workplace density", "Car-free pedestrian streets"] },
      { name: "Intersection density", actions: ["Add street intersections", "Remove cul-de-sacs", "Pedestrian passages in blocks"] },
      { name: "Population density", actions: ["Increase residential density", "Upzone single-family areas", "Upzone near transit", "Cut parking requirements", "Add a transit station"] },
      { name: "Land-use mix", actions: ["Add residential buildings", "Add non-residential buildings", "Mixed land-use zoning"] },
    ],
  },
  {
    group: "Liveability",
    indicators: [
      { name: "Access to amenities", actions: ["Improve access to amenities (9 types, e.g. schools, sports, pharmacies)"] },
      { name: "Traffic safety", actions: ["Roundabouts", "Street lighting", "Lower speed limits", "Speed cameras", "Traffic calming"] },
      { name: "Outdoor air quality", actions: ["Car-free days", "Limited traffic zones"] },
      { name: "Access to green space", actions: ["Expand green spaces", "Better green-space facilities", "Greenways and trails", "Green-space campaigns"] },
      { name: "Urban crime rate", actions: ["Improve public lighting"] },
    ],
  },
];

// Section 4 on wide screens: groups per column, balanced by number of rows
const DATA_COLUMNS = [["Population", "Mobility"], ["Health", "Emissions"]];

// Section 5: glossary. A definition may have several paragraphs.
const GLOSSARY = [
  {
    group: "General Terms",
    tag: "Maps · Projects",
    terms: [
      { term: "Action", definition: ["Any urban design intervention, policy or investment initiated by urban public officials with the intention to enhance liveability and mobility and provide some contribution to climate mitigation or adaptation."] },
      { term: "Indicator", definition: ["A direct or indirect measure that shows what a situation is like. An indicator always represents part of a system."] },
      { term: "Start year of project", definition: ["Year of the project implementation."] },
      { term: "Project assessment period", definition: ["Number of years over which the economic benefits of the project are accumulated. A longer assessment period increases the cumulative economic benefits (VSL and VOLY)."] },
      { term: "Project area", definition: ["The selected area within the project simulation that an action will be implemented upon. The area is made of one or more 1km² grid cells, and the actions are applied to all of them."] },
      { term: "15-minute city", definition: ["The 15-minute city weaves together strands of climate action and people-centric urban development to ensure that residents of all ages, backgrounds and abilities can meet their daily needs locally, in a short walk or bike ride from home, as well as improve public transport across the city."] },
    ],
  },
  {
    group: "Health Terms",
    tag: "Projects",
    terms: [
      { term: "Death averted", definition: ["A valid and meaningful way of capturing some important aspects of mortality, across the population in any given year and related to current levels of physical activity. Important to note that deaths are not ‘saved’ by increased physical activity but rather shifted to older ages."] },
      { term: "Mortality", definition: ["Death number or rate in a population."] },
      { term: "Morbidity", definition: ["Disease number or rate in a population."] },
      { term: "WHO Physical activity target", definition: ["Adults aged 18–64 should do at least 150 minutes of moderate-intensity aerobic physical activity throughout the week."] },
      { term: "Years lived with disability (YLD)", definition: ["Measures the number of years persons are burdened by living with a disease or disability. YLD is determined by the number of years disabled weighted by level of disability caused by a disability or disease."] },
    ],
  },
  {
    group: "Mobility Terms",
    tag: "Projects",
    terms: [
      { term: "Average trip duration", definition: ["Average time (in minutes) of walking or cycling per person for each trip."] },
      { term: "Average trip distance", definition: ["Average length of walking or cycling per person per trip."] },
      { term: "Average trip speed", definition: ["Average speed of walking or cycling per person per trip, taking into account stops at crossing or due to obstacles. The WHO recommended average walking speed is 5.3 km/h, and cycling speed is 14.0 km/h."] },
      { term: "Mode share", definition: ["The percentage of all trips that are made using a particular mode of transport (e.g. walking, cycling, car)."] },
      { term: "Mode shift", definition: ["The percentage switch from one transport mode to another, e.g switching from driving to walking."] },
      { term: "Trip", definition: ["One way journey, e.g commuting from home to the office."] },
      { term: "Vehicle load factor", definition: ["Average occupancy of vehicles."] },
    ],
  },
  {
    group: "Economic Terms",
    tag: "Projects",
    terms: [
      { term: "CAPEX", definition: ["Capital expenditure, or CAPEX, are funds used to acquire, upgrade, and maintain physical assets such as property, industrial buildings, or equipment."] },
      { term: "Discount rate", definition: ["Since economic benefits occurring in the future are generally considered less valuable than those occurring in the present, economists apply a discount rate to future benefits. Common discount rates are usually available from governments. The WHO advises to use 5% as a default value."] },
      { term: "OPEX", definition: ["Operating expenses are the costs for running the operations on a daily basis."] },
      { term: "Value of life year (VOLY)", definition: ["The monetary value of a year of life lost. It is based on studies that assess the willingness to pay for reducing mortality risks associated with air pollution."] },
      { term: "Value of statistical life (VSL)", definition: ["The benefit of preventing a fatality, defined as the additional cost that individuals would be willing to bear for improvements in safety/reductions in risk that reduce the expected number of fatalities by one."] },
      { term: "Difference between VOLY and VSL", definition: [
        "VSL is the monetary value assigned to a \"whole\" life, whereas VOLY is the value for a single year of life. VSL will therefore be much greater than VOLY.",
        "You can think of VSL as how much people are willing to pay to prevent a fatality and VOLY as how much people are willing to pay to live for an extra year.",
      ] },
    ],
  },
];

// Section 3.1: the two multipliers linking the index to walking and cycling
const KEY_FIGURES = [
  {
    mode: "Walking",
    effect: "+9 minutes of walking per walk among the population",
    reference: "Hirsch et al. (2013). Walk Score® and Transit Score® and walking in the multi-ethnic study of atherosclerosis. Am J Prev Med, Aug;45(2):158-66. doi: 10.1016/j.amepre.2013.03.018",
    url: "https://pubmed.ncbi.nlm.nih.gov/23867022/",
  },
  {
    mode: "Cycling",
    effect: "+0.5% cycling mode share",
    reference: "Winters et al. (2016). Bike Score®: Associations between urban bikeability and cycling behavior in 24 cities. International Journal of Behavioral Nutrition and Physical Activity. DOI 10.1186/s12966-016-0339-0",
    url: "https://open.library.ubc.ca/media/stream/pdf/52383/1.0308593/3",
  },
];

const METHODOLOGY_URL = "https://www.c40.org/wp-content/uploads/2022/02/C40-Walking-and-Cycling-Benefits-Tool-Methodology.pdf";

// Top-level tabs. "tag" says which part of the tool each tab explains
const VIEWS = [
  { id: "indicators", kind: "main", tag: "Maps · Projects", short: "Indicators & scores", title: "Healthy Neighbourhood Explorer indicators: description and data sources", hash: "indicators-and-scores" },
  { id: "how", kind: "main", tag: "Projects", short: "How projects work", hash: "how-projects-work" },
  { id: "data", kind: "reference", tag: "Reference", short: "Default data", title: "Default data and assumptions", hash: "default-data" },
  { id: "glossary", kind: "reference", tag: "Reference", short: "Glossary", title: "Terminology", hash: "glossary" },
];

// The steps of the chain inside "How it works", in the order of the calculation
const STEPS = [
  { hash: "actions", short: "Actions", sub: "What you change in the area", title: "Urban actions and their impact on indicators" },
  { hash: "indicators", short: "Indicators & scores", sub: "Before and after the actions", title: "How actions change the indicators and the Healthy Neighbourhood score" },
  { hash: "physical-activity", short: "Physical activity", sub: "Change in walking and cycling", title: "Relationship between indicator scores and physical activity" },
  { hash: "benefits", short: "Benefits", sub: "Health, economic and emissions", title: "How the health, economic and emissions benefits are calculated" },
];

// Step 4: summary of the calculation of each benefit (methodology report, Dec 2018, pages 7, 10 and 11)
const BENEFITS = [
  {
    kind: "health",
    title: "Health",
    result: "Deaths averted, life years gained and change in years lived with disability",
    steps: [
      "Extra minutes of walking and cycling per week lower the risk of dying, using the WHO HEAT relative risks: 0.89 for walking (about 170 min/week) and 0.90 for cycling (100 min/week).",
      "To avoid inflating results, activity is capped at 460 min/week for walking and 447 for cycling, and the risk reduction at 30% and 45%.",
      "Applied to national mortality rates by age (life-table approach), this gives the deaths averted and life years gained.",
      "Illness uses ITHIM risk reductions for 7 diseases: coronary heart disease, stroke, dementia, depression, type 2 diabetes, breast and colon cancer.",
    ],
  },
  {
    kind: "economic",
    title: "Economic",
    result: "Value of the health benefits over the project life",
    steps: [
      "Deaths averted are valued with the Value of Statistical Life (VSL).",
      "Life years gained are valued with the Value of a Life Year (VOLY).",
      "Both are accumulated over the assessment period and discounted, since future benefits are worth less than present ones.",
      "Implementation costs (CAPEX and OPEX) are discounted in the same way, so benefits and costs can be compared.",
    ],
  },
  {
    kind: "emissions",
    title: "Emissions",
    result: "Kilograms of greenhouse gases and pollutants avoided",
    steps: [
      "The new walking and cycling trips, the mode shift and the vehicle load factor give the number of car trips avoided.",
      "Multiplied by the journey length, this gives the kilometres of car trips avoided.",
      "Multiplied by an emission factor (kg per km, by fuel type and vehicle fuel efficiency), this gives the emissions avoided.",
    ],
  },
];

// Section 2: the 9 indicators, their maximum score and main data source (from the indicators table, Oct/2023)
// Each group's maximum scores add up to 100
// type: osm = OpenStreetMap, public = other public dataset, user = data added by the user
const INDICATORS = [
  {
    group: "Mobility",
    items: [
      { name: "Retail quality", max: 25, type: "osm", source: "OpenStreetMap" },
      { name: "Intersection density", max: 25, type: "osm", source: "OpenStreetMap" },
      { name: "Population density", max: 25, type: "public", source: "GHSL (European Commission)" },
      { name: "Land-use mix", max: 25, type: "osm", source: "OpenStreetMap" },
    ],
  },
  {
    group: "Liveability",
    items: [
      { name: "Access to amenities", max: 70, type: "osm", source: "OpenStreetMap" },
      { name: "Traffic safety", max: 5, type: "public", source: "ITF Safer City Streets / WRI" },
      { name: "Outdoor air quality", max: 10, type: "public", source: "Washington University (ACAG)" },
      { name: "Access to green space", max: 10, type: "osm", source: "OpenStreetMap" },
      { name: "Urban crime rate", max: 5, type: "user", source: "User data" },
    ],
  },
];

// Prominent download card for the reference tables
// Opens one of the reference tables (src/content/tables/*.md) in a modal
function TableCard({ kind, title, description }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="db-pdf-card" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <span className="db-pdf-card-icon"><TableChartOutlinedIcon /></span>
        <span className="db-pdf-card-text">
          <span className="db-pdf-card-title">{title}</span>
          <span className="db-pdf-card-desc">{description}</span>
        </span>
        <span className="db-pdf-card-open"><OpenInFullIcon /></span>
      </button>
      <ReferenceTableDialog kind={kind} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function PdfButton({ href, children }) {
  return (
    <Button component="a" href={href} target="_blank" rel="noopener noreferrer" variant="outlined" className="db-pdf-button" startIcon={<PictureAsPdfOutlinedIcon />}>
      {children}
    </Button>
  );
}

function ExternalLink({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="db-external-link">
      {children}
      <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "-2px" }} />
    </a>
  );
}

function Glossary() {
  const [openTerms, setOpenTerms] = useState([]);

  const toggleTerm = (term) =>
    setOpenTerms((prev) => (prev.includes(term) ? prev.filter((t) => t !== term) : [...prev, term]));

  return (
    <div className="db-glossary-groups">
      {GLOSSARY.map((g) => (
        <Box key={g.group}>
          <h3 className="db-subheading">{g.group} <span className="db-subheading-max">{g.tag}</span></h3>
          {g.terms.map((t) => (
            <Accordion
              key={t.term}
              className="db-term"
              expanded={openTerms.includes(t.term)}
              onChange={() => toggleTerm(t.term)}
              disableGutters
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                <span className="db-term-name">{t.term}</span>
              </AccordionSummary>
              <AccordionDetails>
                {t.definition.map((d, i) => <p key={i}>{d}</p>)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </div>
  );
}

// Indicators & scores: shown in its own tab (for Maps users) and as step 2 of "How projects work"
// Step 2: worked example, from a run of the dev database (2026-10-09): childcare access raised to level 3
// ("optimal") in a set of grid cells. Liveability/Mobility up to 100, General = Mobility x Liveability
const ACTION_EXAMPLE = [
  { label: "Access to amenities", note: "indicator: amenity points, up to 27", before: "11", after: "14", change: "+3" },
  { label: "Access to amenities score", note: "up to 70", before: "28.6", after: "36.4", change: "+7.8" },
  { label: "Liveability score", note: "up to 100", before: "42.8", after: "50.6", change: "+7.8" },
  { label: "Mobility score", note: "up to 100, not affected", before: "63.0", after: "63.0", change: "0" },
  { label: "General score", note: "Mobility × Liveability, up to 10,000", before: "2,699", after: "3,190", change: "+491" },
  { label: "Healthy Neighbourhood index", note: "average of Mobility and Liveability", before: "52.9", after: "56.8", change: "+3.9", key: true },
];

function ActionImpactContent({ goIndicators, stepLink }) {
  return (
    <div className="db-two-col">
      <div>
      <p>Actions don’t produce benefits directly: they change the indicators of the selected grid cells, and the change in the Healthy Neighbourhood score is what drives walking and cycling.</p>
      <ol className="db-steps">
        <li>
          <strong>Actions change the indicators.</strong> Each action changes one indicator (see {stepLink(1, "Actions")}). The tool keeps its value <em>before</em> and <em>after</em> the actions.
        </li>
        <li>
          <strong>Both values are turned into scores</strong>, with the same thresholds and weights used on the Maps (see <a href="#indicators-and-scores" onClick={(e) => { e.preventDefault(); goIndicators(); }}>Indicators &amp; scores</a>).
        </li>
        <li>
          <strong>The scores are recomputed.</strong> Mobility and Liveability (up to 100 each) and the General score (Mobility × Liveability, up to 10,000) are calculated before and after. These are the before/after values shown in the project results.
        </li>
        <li>
          <strong>The change in the index drives physical activity.</strong> The difference between before and after in the Healthy Neighbourhood index (the average of Mobility and Liveability, in points) is converted into extra walking and cycling in {stepLink(3, "Physical activity")}.
        </li>
      </ol>
      </div>
      <div>
      <h3 className="db-subheading">Example: improve access to childcare</h3>
      <p className="db-ba-caption">Action: <strong>Improve access to amenities</strong>, childcare raised to <em>optimal</em> (3 or more within a 15-minute walk) in a set of grid cells.</p>
      <table className="db-ba">
        <thead>
          <tr><th scope="col"></th><th scope="col">Before</th><th scope="col">After</th><th scope="col">Change</th></tr>
        </thead>
        <tbody>
          {ACTION_EXAMPLE.map((r) => (
            <tr key={r.label} className={r.key ? "db-ba-key" : undefined}>
              <th scope="row">{r.label}<small>{r.note}</small></th>
              <td>{r.before}</td>
              <td>{r.after}</td>
              <td><span className={"db-ba-change" + (r.change === "0" ? " db-ba-none" : "")}>{r.change}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="db-ba-next">+3.9 index points → extra walking and cycling trips in {stepLink(3, "Physical activity ›")}</p>
      </div>
    </div>
  );
}

function IndicatorsContent() {
  return (
    <>
    <p className="db-context">These are the scores shown on the Maps, and the scores that project actions change.</p>
    <div className="db-two-col">
      <div>
      <p>The Healthy Neighbourhood Index is a weighted score made of <strong>9 indicators transformed into scores</strong>: 4 related to mobility and 5 related to liveability. Each score is built in two steps:</p>
      <ol className="db-steps">
        <li>
          <strong>Calculated from the data.</strong> The indicator is measured for each 1km² grid cell from its data source. For example, intersection density is the number of road intersections per km², counted from OpenStreetMap.
        </li>
        <li>
          <strong>Converted into a score.</strong> The measured value is compared with set thresholds and turned into a score. For example, an intersection density is scaled between 0 and 25, and there is thresholds of 750 intersections per km² that gets the top score of 25.
        </li>
      </ol>
      <p>The indicator scores are then combined into the mobility and liveability scores that make up the index.</p>
      <div className="db-formula" aria-label="General score = Mobility score times Liveability score">
        <span className="db-formula-term">Mobility<small>up to 100</small></span>
        <span className="db-formula-op">×</span>
        <span className="db-formula-term">Liveability<small>up to 100</small></span>
        <span className="db-formula-op">=</span>
        <span className="db-formula-term db-formula-result">General score<small>up to 10,000</small></span>
      </div>
      <TableCard kind="indicators" title="Indicators table" description="Data source, calculation and scoring thresholds of every indicator" />
      </div>
      <div>
      <div className="db-indicator-groups">
        {INDICATORS.map((g) => (
          <div key={g.group} className="db-indicator-group">
            <h3 className="db-subheading">
              {g.group} score <span className="db-subheading-max">up to {g.items.reduce((sum, i) => sum + i.max, 0)}</span>
            </h3>
            <div className="db-chips">
              {g.items.map((i) => (
                <span key={i.name} className={`db-chip db-chip-${i.type}`}>
                  <span className="db-chip-top">
                    <span className="db-chip-name">{i.name}</span>
                    <span className="db-chip-max" title={`Scores up to ${i.max}`}>{i.max}</span>
                  </span>
                  <span className="db-chip-source">{i.source}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <ul className="db-legend">
        <li><span className="db-dot db-chip-osm" /><strong>OpenStreetMap</strong>: built by a community of mappers who contribute and maintain the data, so its quality may vary between cities.</li>
        <li><span className="db-dot db-chip-public" /><strong>Other public datasets</strong>: trusted, publicly available global datasets.</li>
        <li><span className="db-dot db-chip-user" /><strong>User data</strong>: there is no consistent, globally representative dataset for urban crime, so users add their own data.</li>
      </ul>
      </div>
    </div>
    </>
  );
}

export default function DatabasePage() {
  // view: "indicators", "how" (with step 0 = overview, 1-4 = a step of the chain), "data" or "glossary"
  const [view, setView] = useState("indicators");
  const [step, setStep] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [view, step]);

  // Scrolling down over the panel moves the page first, until the tabs reach the top of the
  // window; only then does the panel's own content scroll. (Needs a non-passive listener.)
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const onWheel = (e) => {
      if (e.deltaY <= 0 || panel.scrollHeight <= panel.clientHeight) return;
      const sectionsTop = document.getElementById("db-sections").getBoundingClientRect().top;
      const pageCanScroll = window.scrollY < document.documentElement.scrollHeight - window.innerHeight - 1;
      if (sectionsTop > 17 && pageCanScroll) {
        e.preventDefault();
        window.scrollBy(0, Math.min(e.deltaY, sectionsTop - 16));
      }
    };
    panel.addEventListener("wheel", onWheel, { passive: false });
    return () => panel.removeEventListener("wheel", onWheel);
  }, []);

  const go = (nextView, nextStep = 0) => {
    setView(nextView);
    setStep(nextStep);
    const hash = nextView === "how" && nextStep ? STEPS[nextStep - 1].hash : VIEWS.find((v) => v.id === nextView).hash;
    window.history.replaceState(null, "", `#${hash}`);
    // scroll only as far as needed to show the whole panel
    setTimeout(() => document.getElementById("db-sections")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  // open the part named in the URL, e.g. /databaseterms#benefits
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const s = STEPS.findIndex((x) => x.hash === hash);
    const v = VIEWS.find((x) => x.hash === hash);
    if (s >= 0) go("how", s + 1);
    else if (v) go(v.id);
  }, []);

  const stepLink = (n, label) => (
    <a href={`#${STEPS[n - 1].hash}`} onClick={(e) => { e.preventDefault(); go("how", n); }}>{label || STEPS[n - 1].short}</a>
  );

  const current = view === "how" ? (step ? STEPS[step - 1] : null) : VIEWS.find((v) => v.id === view);

  return (
    <div className="container">
      <h5 style={{marginBottom: "32px"}}>Database Sources, References and Terms</h5>
      <div className="db-intro">
      <div className="db-intro-main">
      <p>This page explains the data and methods behind the two parts of the Healthy Neighbourhood Explorer:</p>
      <div className="db-parts">
        <div className="db-part">
          <div className="db-part-name">Maps</div>
          <p>Explore the current Healthy Neighbourhood scores of all urban agglomerations over 500,000 people, cell by cell (1km² grid).</p>
          <a href="#indicators-and-scores" onClick={(e) => { e.preventDefault(); go("indicators"); }}>Behind the maps: Indicators & scores ›</a>
        </div>
        <div className="db-part">
          <div className="db-part-name">Projects <span className="db-part-tag">after login</span></div>
          <p>Simulate urban actions in a neighbourhood and estimate their health, economic and emissions benefits.</p>
          <a href="#how-projects-work" onClick={(e) => { e.preventDefault(); go("how"); }}>How project results are calculated ›</a>
        </div>
      </div>
      <p className="db-small">
        <a target="_blank" rel="noopener noreferrer" href={METHODOLOGY_URL}>Methodology report</a> (C40 Walking & Cycling Benefits tool)
        {" · "}<a target="_blank" rel="noopener noreferrer" href="https://drive.google.com/file/d/1a7sZPyg_e9GPagK5qAyJlF_PzXxU1tBV/view">FAQ</a>
        {" · "}Feedback: <a href="mailto:healthyneighbourhood@C40.org">healthyneighbourhood@C40.org</a>
      </p>
      </div>
      <Alert severity="info" className="db-callout">
        <AlertTitle>Global data, local context</AlertTitle>
        <p>The Healthy Neighbourhood Explorer is built on global databases and on relationships taken from international research, so that it can be applied consistently across cities. Cities may hold local data (for example travel surveys, health statistics or emission factors) and locally derived relationships that are more accurate for their context than these global defaults.</p>
        <p>Results from this tool are modelled results providing the scale of potential impacts within a local community. These should not be relied upon for investment decisions.
        Local factors and contexts can mean that the beneficial outcomes are not realised as predicted by models.</p>
      </Alert>
      </div>

      {/* separates the overview above from the tabbed reference below */}
      <div className="db-divider" role="separator"><span>Explore the methodology</span></div>

      <div id="db-sections" className="db-sections">
        <div className="db-tabs" role="tablist" aria-label="Sections">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              id={`view-tab-${v.id}`}
              aria-selected={view === v.id}
              aria-controls="db-panel"
              className={`db-tab db-tab-${v.kind}${view === v.id ? " active" : ""}`}
              onClick={() => go(v.id)}
            >
              <span className="db-tab-text">
                <span className="db-tab-eyebrow">{v.tag}</span>
                <span className="db-tab-label">{v.short}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="db-panel" id="db-panel" ref={panelRef} role="tabpanel" aria-labelledby={`view-tab-${view}`}>
          {view === "how" && (
            <nav className="db-chain" aria-label="Steps of the calculation">
              {STEPS.map((s, i) => (
                <Fragment key={s.hash}>
                  {i > 0 && <span className="db-chain-arrow" aria-hidden="true" />}
                  <button
                    type="button"
                    className={`db-chain-step${step === i + 1 ? " active" : ""}`}
                    aria-current={step === i + 1 ? "step" : undefined}
                    onClick={() => go("how", i + 1)}
                  >
                    <span className="db-chain-number">{i + 1}</span>
                    <span className="db-chain-text">
                      <span className="db-chain-label">{s.short}</span>
                      <span className="db-chain-sub">{s.sub}</span>
                    </span>
                  </button>
                </Fragment>
              ))}
            </nav>
          )}

          {current && (
            <div className="db-panel-head">
              <h6 className="db-panel-title">{current.title}</h6>
              {view === "how" && step > 0 && (
                <div className="db-step-nav">
                  {step > 1
                    ? <button type="button" className="db-prev" onClick={() => go("how", step - 1)}>‹ {STEPS[step - 2].short}</button>
                    : <button type="button" className="db-prev" onClick={() => go("how", 0)}>‹ Overview</button>}
                  {step < STEPS.length && <button type="button" className="db-next" onClick={() => go("how", step + 1)}>{STEPS[step].short} ›</button>}
                </div>
              )}
            </div>
          )}

          {view === "how" && step === 0 && (
            <div className="db-two-col">
              <div>
              <p>In a project, the tool follows a chain of cause and effect, shown above. <strong>Click a step</strong> to see how it is calculated:</p>
              <ol className="db-overview-list">
                <li>{stepLink(1, "Actions")}: you choose actions in a neighbourhood, such as adding retail space or improving access to amenities.</li>
                <li>{stepLink(2, "Indicators & scores")}: the actions change the indicator scores of the area.</li>
                <li>{stepLink(3, "Physical activity")}: better scores make walking and cycling more attractive, so physical activity increases.</li>
                <li>{stepLink(4, "Benefits")}: the extra activity brings health benefits, which have an economic value, and replaces trips in polluting vehicles, which reduces emissions.</li>
              </ol>
              </div>
              <div>
              <p>Each causal link uses quantitative factors, often described as multipliers, from peer-reviewed literature. This literature analyses multiple case studies and, through meta-analysis, provides generalisable relationships.</p>
              <button type="button" className="db-next" onClick={() => go("how", 1)}>Start with step 1: Actions ›</button>
              </div>
            </div>
          )}

          {view === "how" && step === 1 && (
            <div className="db-two-col">
              <div>
              <p>The Healthy Neighbourhood Explorer can model the health and emissions benefits of 26 urban actions. Each action raises the relevant indicator score through a multiplier relationship. The impact level of an action is set in one of two ways:</p>
              <ul>
                <li>For some actions you enter the size of the change. For example, for ‘Add retail space’ you enter the number of new retail units added within the selected grid.</li>
                <li>For other actions the impact levels are pre-defined, and you select one of them.</li>
              </ul>
              <TableCard kind="actions" title="Actions table" description="Every action, its calculation logic and its impact levels" />
              </div>
              <div>
              <h3 className="db-subheading">Actions by indicator</h3>
              <div className="db-action-summary">
                {ACTIONS_BY_INDICATOR.map((g) => (
                  <div key={g.group} className="db-action-group">
                    <div className="db-action-group-name">{g.group}</div>
                    {g.indicators.map((ind) => (
                      <div key={ind.name} className="db-action-row">
                        <div className="db-action-indicator">
                          <span>{ind.name}</span>
                          <span className="db-chip-max" title={`${ind.actions.length} action${ind.actions.length > 1 ? "s" : ""}`}>{ind.actions.length}</span>
                        </div>
                        <div className="db-action-names">{ind.actions.join(" · ")}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              </div>
            </div>
          )}

          {view === "how" && step === 2 && <ActionImpactContent goIndicators={() => go("indicators")} stepLink={stepLink} />}

          {view === "how" && step === 3 && (
            <div className="db-two-col">
              <div>
              <p>The health and GHG emissions benefits of an action come from the extra walking and cycling it triggers. Actions raise the combined index score of the area, which makes walking and cycling more attractive transport options for residents. The figures below link the index to walking and cycling:</p>
              <div className="db-key-figures">
                {KEY_FIGURES.map((f) => (
                  <div className="db-key-figure" key={f.mode}>
                    <div className="db-key-figure-mode">{f.mode}</div>
                    <div className="db-key-figure-cause">+10 points in the Healthy Neighbourhood index</div>
                    <div className="db-key-figure-arrow" aria-hidden="true">↓</div>
                    <div className="db-key-figure-effect">{f.effect}</div>
                    <div className="db-key-figure-ref"><ExternalLink href={f.url}>{f.reference}</ExternalLink></div>
                  </div>
                ))}
              </div>
              </div>
              <div>
              <h3 className="db-subheading">What the tool calculates</h3>
              <ul className="db-check-list">
                <li><strong>Extra walking and cycling trips</strong> per day and per year in the selected grid cells, from the change in the index.</li>
                <li><strong>Number of people more active</strong>: the extra walking and cycling trips per year divided by the trips a user makes per year (250 by default, from WHO HEAT).</li>
                <li><strong>Increase in physical activity of the community</strong>: the total extra minutes of walking and cycling per week, from the number and duration of the new trips (cycling trips: the city's average cycling journey length at 14 km/h).</li>
                <li><strong>Share of the WHO physical activity target</strong> (150 minutes per week) that these minutes represent.</li>
              </ul>
              <p>These results feed the {stepLink(4, "benefits")} calculations, which are run separately for walking and cycling (each with its own relative risk) and then added up. Emissions avoided count only the new trips, using the city's mix of car, taxi and public transport trips they replace.</p>
              </div>
            </div>
          )}

          {view === "how" && step === 4 && (
            <>
              <div className="db-benefits">
                {BENEFITS.map((b) => (
                  <div key={b.title} className={`db-benefit db-benefit-${b.kind}`}>
                    <h3 className="db-benefit-title">{b.title}</h3>
                    <p className="db-benefit-result">{b.result}</p>
                    <ol className="db-benefit-steps">
                      {b.steps.map((t) => <li key={t}>{t}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
              <div className="db-benefits-footer">
                <p className="db-muted"><strong>Limits of the method:</strong> results apply to adults (20–74 years for walking, 20–64 for cycling) and to habitual walking and cycling, such as commuting. Most default values come from US and European studies. Road accidents are not included.</p>
                <PdfButton href={METHODOLOGY_URL}>Methodology report (PDF)</PdfButton>
              </div>
            </>
          )}

          {view === "indicators" && <IndicatorsContent />}

          {view === "data" && (
            <p className="db-context">This data is used in the project calculations (physical activity and benefits). The Maps use the indicator data sources listed in <a href="#indicators-and-scores" onClick={(e) => { e.preventDefault(); go("indicators"); }}>Indicators & scores</a>.</p>
          )}
          {view === "data" && (
            <div className="db-data-columns">
            {DATA_COLUMNS.map((col) => (
            <div key={col.join()}>
            {DEFAULT_DATA.filter((g) => col.includes(g.group)).map((g) => (
            <div key={g.group} className="db-data-group">
              <h3 className="db-subheading">{g.group}</h3>
              <dl className="db-data-list">
                {g.rows.map((row) => (
                  <div className="db-data-row" key={row.item}>
                    <dt>{row.item}</dt>
                    <dd>{row.url ? <ExternalLink href={row.url}>{row.source}</ExternalLink> : row.source}</dd>
                  </div>
                ))}
              </dl>
            </div>
            ))}
            </div>
            ))}
            </div>
          )}

          {view === "glossary" && <Glossary />}
        </div>
      </div>
    </div>
  );
}
